import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import Services from '../services/Services';
import { User, UserAuthorization } from '../types/auth.types';
import { broadcastAuthMessage, subscribeAuthMessage, isRedirectSuppressed, AuthMessage } from '../utils/authChannel';
import { setLoggingOutFlag, cancelAllRequests, resetLoggingOutFlag } from '../utils/requestCancellation';

interface AuthContextType {
  token: string | null;
  apiToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isSubscriptionExpired: boolean;
  subscriptionRenewDate: string | null;
  subscriptionType: string | null;
  userAuthorizations: UserAuthorization[];
  rememberMe: boolean;
  login: (credential: string, password: string, rememberMe: boolean, redirectUrl?: string) => Promise<void>;
  register: (name: string, lastname: string, email: string, password: string, redirectUrl?: string) => Promise<void>;
  logout: () => void;
  updateToken: (token: string) => void;
  updateUser: (updates: Partial<User>) => void;
  getUserAuthorizations: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// API credentials for getting initial token
const TOKEN_API_KEY = process.env.REACT_APP_TOKEN_API_KEY || '';
const TOKEN_API_SECRET = process.env.REACT_APP_TOKEN_API_SECRET || '';

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubscriptionExpired, setIsSubscriptionExpired] = useState(false);
  const [subscriptionRenewDate, setSubscriptionRenewDate] = useState<string | null>(null);
  const [subscriptionType, setSubscriptionType] = useState<string | null>(null);
  const [userAuthorizations, setUserAuthorizations] = useState<UserAuthorization[]>([]);
  const [rememberMe, setRememberMe] = useState(false);
  const [apiToken, setApiToken] = useState<string | null>(null);
  const navigate = useNavigate();
  const isLoggingOutRef = useRef(false);

  // Fetch user authorizations function
  const fetchUserAuthorizations = useCallback(async (userToken: string, userID: string) => {
    try {
      // Get API token if not available
      let currentApiToken = apiToken;
      if (!currentApiToken && TOKEN_API_KEY && TOKEN_API_SECRET) {
        try {
          const apiTokenResponse = await Services.GetToken(TOKEN_API_KEY, TOKEN_API_SECRET);
          if (apiTokenResponse.data.status) {
            currentApiToken = apiTokenResponse.data.result.token;
            setApiToken(currentApiToken);
          }
        } catch (error) {
          return;
        }
      }
      
      if (!currentApiToken) return;
      const response = await Services.GetUserAuthorizations(currentApiToken, userID);
      if (response.data.status) {
        setUserAuthorizations(response.data.result);
      }
    } catch (error) {
      // Failed to fetch user authorizations
    }
  }, [apiToken]);

  const logout = useCallback(() => {
    // Prevent duplicate logout calls
    if (isLoggingOutRef.current) {
      return;
    }
    isLoggingOutRef.current = true;

    try {
      // 1. Set global flag to prevent new requests
      setLoggingOutFlag(true);

      // 2. Cancel all pending requests
      cancelAllRequests();

      // 3. Clear state (synchronous)
      setToken(null);
      setUser(null);
      setRememberMe(false);
      setIsSubscriptionExpired(false);
      setSubscriptionRenewDate(null);
      setSubscriptionType(null);
      setUserAuthorizations([]);

      // 4. Clear storage
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('username');
      localStorage.removeItem('temp_username');
      localStorage.removeItem('rememberMe');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');

      // 5. Broadcast logout
      broadcastAuthMessage({ type: 'LOGOUT' });

      // 6. Navigate after cleanup with small delay
      setTimeout(() => {
        navigate('/login', { replace: true });
        // Reset flag after navigation
        setTimeout(() => {
          isLoggingOutRef.current = false;
          resetLoggingOutFlag();
        }, 100);
      }, 50);
    } catch (error) {
      // Reset flags on error
      isLoggingOutRef.current = false;
      resetLoggingOutFlag();
    }
  }, [navigate]);

  // Initialize auth state from storage
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Get API token first (don't block if this fails)
        if (TOKEN_API_KEY && TOKEN_API_SECRET) {
          try {
            const apiTokenResponse = await Services.GetToken(TOKEN_API_KEY, TOKEN_API_SECRET);
            if (apiTokenResponse.data.status) {
              setApiToken(apiTokenResponse.data.result.token);
            }
          } catch (tokenError) {
            // Don't throw - allow app to render, token will be fetched on login
          }
        }

        // Check for existing auth
        const sessionToken = sessionStorage.getItem('token');
        const localToken = localStorage.getItem('token');
        const storedToken = localToken || sessionToken;

        const sessionUser = sessionStorage.getItem('user');
        const localUser = localStorage.getItem('user');
        let storedUser = null;
        
        try {
          storedUser = sessionUser ? JSON.parse(sessionUser) : (localUser ? JSON.parse(localUser) : null);
        } catch (parseError) {
          // Failed to parse stored user data
        }

        const storedRememberMe = localStorage.getItem('rememberMe') === 'true';

        if (storedToken && storedUser) {
          setToken(storedToken);
          setUser(storedUser);
          setRememberMe(storedRememberMe);
          setIsSubscriptionExpired(storedUser.isExpired || false);
          setSubscriptionRenewDate(storedUser.subscriptionExpiredDate || null);
          setSubscriptionType(storedUser.subscriptionPackageType || null);

          // Fetch authorizations if we have API token
          // Note: fetchUserAuthorizations will fetch API token if needed
          await fetchUserAuthorizations(storedToken, storedUser.id);
        }
      } catch (error) {
        // Failed to initialize auth
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Listen for cross-tab auth messages
  useEffect(() => {
    const unsubscribe = subscribeAuthMessage((message: AuthMessage) => {
      if (message.type === 'LOGIN') {
        if (message.token && message.user) {
          setToken(message.token);
          setUser(message.user);
          setRememberMe(message.rememberMe || false);
          setIsSubscriptionExpired(message.user.isExpired || false);
          setSubscriptionRenewDate(message.user.subscriptionExpiredDate || null);
          setSubscriptionType(message.user.subscriptionPackageType || null);

          // Fetch authorizations
          fetchUserAuthorizations(message.token, message.user.id);

          // Don't navigate in cross-tab scenarios - the tab that initiated login already navigated
          // Cross-tab messages are only for syncing auth state, not for navigation
          // This prevents "operation is insecure" errors from BroadcastChannel context
        }
      } else if (message.type === 'LOGOUT') {
        logout();
      }
    });

    return unsubscribe;
  }, [navigate, fetchUserAuthorizations, logout]);

  const getUserAuthorizations = useCallback(async () => {
    if (token && user) {
      await fetchUserAuthorizations(token, user.id);
    }
  }, [token, user, fetchUserAuthorizations]);

  const login = useCallback(async (credential: string, password: string, remember: boolean, redirectUrl?: string) => {
    // Fetch API token if not available
    let currentApiToken = apiToken;
    if (!currentApiToken) {
      if (!TOKEN_API_KEY || !TOKEN_API_SECRET) {
        throw new Error('API credentials not configured. Please set REACT_APP_TOKEN_API_KEY and REACT_APP_TOKEN_API_SECRET');
      }
      try {
        const apiTokenResponse = await Services.GetToken(TOKEN_API_KEY, TOKEN_API_SECRET);
        if (apiTokenResponse.data.status) {
          currentApiToken = apiTokenResponse.data.result.token;
          setApiToken(currentApiToken);
        } else {
          throw new Error('Failed to get API token');
        }
      } catch (error) {
        throw new Error('Failed to authenticate. Please check your API configuration.');
      }
    }

    const response = await Services.UserLogin(currentApiToken, credential, password);
    
    if (response.data.status && response.data.result) {
      const userData = response.data.result;
      
      setToken(userData.token);
      setUser(userData);
      setRememberMe(remember);
      setIsSubscriptionExpired(userData.isExpired || false);
      setSubscriptionRenewDate(userData.subscriptionExpiredDate || null);
      setSubscriptionType(userData.subscriptionPackageType || null);

      // Store based on remember me
      if (remember) {
        localStorage.setItem('token', userData.token);
        localStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('rememberMe', 'true');
        if (credential) {
          localStorage.setItem('username', credential);
        }
      } else {
        sessionStorage.setItem('token', userData.token);
        sessionStorage.setItem('user', JSON.stringify(userData));
        localStorage.setItem('rememberMe', 'false');
        if (credential) {
          localStorage.setItem('temp_username', credential);
        }
      }

      // Fetch authorizations
      await fetchUserAuthorizations(userData.token, userData.id);

      // Broadcast to other tabs (suppress redirect in other tabs to avoid navigation errors)
      broadcastAuthMessage({
        type: 'LOGIN',
        token: userData.token,
        user: userData,
        rememberMe: remember,
        redirectUrl: redirectUrl,
        suppressRedirect: true // Suppress navigation in other tabs - they should just sync auth state
      });

      // Navigate unless suppressed
      if (!isRedirectSuppressed()) {
        // Navigate to redirect URL if provided, otherwise go to homepage
        let targetUrl = '/';
        if (redirectUrl) {
          try {
            const decoded = decodeURIComponent(redirectUrl);
            // Validate that it's a relative path (starts with /) and doesn't contain protocol
            if (decoded && typeof decoded === 'string' && decoded.startsWith('/') && !decoded.startsWith('http://') && !decoded.startsWith('https://')) {
              // Additional validation: ensure it's a valid path
              if (decoded.length > 0 && decoded.length < 2048) {
                targetUrl = decoded;
              }
            }
          } catch (error) {
            targetUrl = '/';
          }
        }
        try {
          navigate(targetUrl);
        } catch (error) {
          // Fallback to homepage on error
          navigate('/');
        }
      }
    } else {
      throw new Error(response.data.message || 'Login failed');
    }
  }, [apiToken, navigate, fetchUserAuthorizations]);

  const register = useCallback(async (name: string, lastname: string, email: string, password: string, redirectUrl?: string) => {
    if (!apiToken) {
      throw new Error('API token not available');
    }

    const response = await Services.UserRegister(apiToken, name, lastname, email, password);
    
    if (response.data.status) {
      // Auto-login after registration with redirect URL
      await login(email, password, true, redirectUrl);
    } else {
      throw new Error(response.data.message || 'Registration failed');
    }
  }, [apiToken, login]);

  const updateToken = useCallback((newToken: string) => {
    setToken(newToken);
    if (rememberMe) {
      localStorage.setItem('token', newToken);
    } else {
      sessionStorage.setItem('token', newToken);
    }
  }, [rememberMe]);

  const updateUser = useCallback((updates: Partial<User>) => {
    setUser(prev => {
      if (!prev) return prev;
      const updated = { ...prev, ...updates };
      // Sync to storage
      if (rememberMe) {
        localStorage.setItem('user', JSON.stringify(updated));
      } else {
        sessionStorage.setItem('user', JSON.stringify(updated));
      }
      return updated;
    });
  }, [rememberMe]);

  const value: AuthContextType = {
    token,
    apiToken,
    user,
    isAuthenticated: !!token,
    isLoading,
    isSubscriptionExpired,
    subscriptionRenewDate,
    subscriptionType,
    userAuthorizations,
    rememberMe,
    login,
    register,
    logout,
    updateToken,
    updateUser,
    getUserAuthorizations
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

